import { apiCall } from ".";
import { getSession } from "../handlers/getSession";
import { getTenant } from "../handlers/getTenant";

/**
 * Integracion con Workday, a traves de djaguar:
 *
 *   POST <tenant>/api/banamex/workday/cases/
 *
 * Reemplaza al servidor puente que corria aparte (localhost:5555). Ahora la
 * llamada lleva la sesion del tenant como cualquier otra, y djaguar exige el
 * permiso casemanager::banamex::workday::create y un tenant de Banamex.
 *
 * djaguar crea el caso Y lo registra en la sancion en la misma peticion, asi que
 * ya no hay un segundo PATCH desde aqui. Antes de llamar a Workday marca la
 * sancion como "enviando": un segundo clic, otra pestaña u otro usuario reciben
 * 409 en vez de crear un duplicado.
 */

/**
 * Un poco mas que el corte de Gunicorn (60 s), para que llegue la respuesta del
 * servidor antes que el timeout del navegador. Si aun asi se agota, la sancion
 * quedo marcada y un reintento recibe 409, no un duplicado.
 */
const CREATE_CASE_TIMEOUT = 65000;

/**
 * Crea (o simula) el caso de Workday de una sancion.
 *
 * Sin `confirm` djaguar solo resuelve los SOEID y devuelve lo que enviaria. Con
 * `confirm: true` el caso queda permanente y no se puede deshacer desde aqui.
 *
 * Dos personas distintas en un caso:
 *   `soeid`      -> forWorker:   a quien se le asigna el caso
 *   `aboutSoeid` -> aboutWorker: de quien trata el caso (opcional)
 *
 * `retry` solo se acepta cuando el envio anterior quedo sin confirmar (Workday
 * no respondio): djaguar responde 409 con `retry_allowed: true` y el usuario
 * debe revisar en Workday que el caso no exista antes de reenviarlo.
 *
 * Respuestas: 201 con `workday` (el registro guardado en la sancion); 409 si la
 * sancion ya tiene caso o hay un envio en curso o sin confirmar; 502 si Workday
 * lo rechazo o no respondio. Los errores traen `error` con el motivo.
 *
 * @param {{reportId: string, sanctionId: number, soeid: string,
 *          aboutSoeid?: string, title?: string, message?: string,
 *          caseType?: string, confirm?: boolean, retry?: boolean}} params
 */
export const createWorkdayCase = async ({
  reportId,
  sanctionId,
  soeid,
  aboutSoeid,
  title,
  message,
  caseType,
  confirm = false,
  retry = false,
}) => {
  const session = getSession();
  const tenant = getTenant();
  if (!session) throw new Error("Sin sesión");

  const body = { report_identifier: reportId, id: sanctionId, soeid };
  if (aboutSoeid) body.aboutSoeid = aboutSoeid;
  if (title) body.title = title;
  if (message) body.message = message;
  if (caseType) body.caseType = caseType;
  if (confirm) body.confirm = true;
  if (retry) body.retry = true;

  const resp = await apiCall.post(`${tenant}/api/banamex/workday/cases/`, body, {
    timeout: CREATE_CASE_TIMEOUT,
    headers: {
      IdToken: session.IdToken,
      Authorization: `Bearer ${session.AccessToken}`,
    },
  });
  return resp.data;
};
